import React, { useState, useEffect } from 'react';
import { PRIMARY_BG } from '../../theme/brand';
import { v4 as uuid } from 'uuid';
import { Plus, Trash2, ChevronUp, ChevronDown, GripVertical, Copy, Check, RefreshCw, Inbox } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { useEditorStore } from '../../store/editorStore';
import api from '../../api/client';

const API_BASE = `${process.env.REACT_APP_API_URL}/api`;

const FIELD_TYPE_LABELS = {
  text:     'Text',
  email:    'Email',
  phone:    'Phone',
  textarea: 'Textarea',
  select:   'Dropdown',
  checkbox: 'Checkbox',
  radio:    'Radio',
  hidden:   'Hidden',
  submit:   'Submit',
};

const FIELD_TYPES = [
  'text', 'email', 'phone', 'textarea', 'select', 'checkbox', 'radio', 'hidden', 'submit',
];

const FIELD_TYPE_COLORS = {
  text:     PRIMARY_BG,
  email:    '#3b82f6',
  phone:    '#10b981',
  textarea: '#8b5cf6',
  select:   '#f59e0b',
  checkbox: '#06b6d4',
  radio:    '#ec4899',
  hidden:   '#6b7280',
  submit:   '#ef4444',
};

export default function FormProperties({ layer }) {
  const data = layer.data;
  const updateLayerData = useEditorStore((s) => s.updateLayerData);
  const [expandedField, setExpandedField] = useState(null);
  const [colorPickerFor, setColorPickerFor] = useState(null);

  // Always read the LATEST fields from the store — avoids stale-prop bug
  // where two rapid addField() calls would both see fields=[] from the render snapshot.
  function getLiveFields() {
    const state = useEditorStore.getState();
    if (!state.story) return [];
    for (const page of state.story.pages) {
      const l = page.layers.find((l) => l.id === layer.id);
      if (l) return l.data.fields || [];
    }
    return [];
  }

  function update(updates) {
    updateLayerData(layer.id, updates);
  }

  // Use prop for rendering (reactive via Zustand subscription in PropertiesPanel)
  const fields = data.fields || [];
  const style = data.style || {
    backgroundColor: '#0f0f1e',
    backgroundOpacity: 90,
    textColor: '#ffffff',
    accentColor: PRIMARY_BG,
    buttonColor: PRIMARY_BG,
    borderRadius: 12,
  };

  function addField(type) {
    const newField = {
      id: uuid(),
      type,
      name: `field_${Date.now()}`,
      label: type === 'submit' ? 'Submit' : `${FIELD_TYPE_LABELS[type]} Field`,
      placeholder: !['submit', 'checkbox', 'radio', 'hidden'].includes(type) ? `Enter ${type}...` : undefined,
      required: type !== 'submit' && type !== 'hidden',
      options: (type === 'select' || type === 'radio')
        ? [{ label: 'Option 1', value: 'opt1' }, { label: 'Option 2', value: 'opt2' }]
        : undefined,
      defaultValue: type === 'hidden' ? '' : undefined,
    };
    // Use getLiveFields() to get the current array from the store, not the
    // potentially-stale snapshot captured in the last render.
    update({ fields: [...getLiveFields(), newField] });
    setExpandedField(newField.id);
  }

  function updateField(fieldId, updates) {
    update({ fields: getLiveFields().map((f) => f.id === fieldId ? { ...f, ...updates } : f) });
  }

  function removeField(fieldId) {
    update({ fields: getLiveFields().filter((f) => f.id !== fieldId) });
    if (expandedField === fieldId) setExpandedField(null);
  }

  function moveField(fieldId, dir) {
    const liveFields = getLiveFields();
    const idx = liveFields.findIndex((f) => f.id === fieldId);
    if (idx < 0) return;
    const newFields = [...liveFields];
    const target = idx + dir;
    if (target < 0 || target >= newFields.length) return;
    [newFields[idx], newFields[target]] = [newFields[target], newFields[idx]];
    update({ fields: newFields });
  }

  function addOption(fieldId) {
    const field = getLiveFields().find((f) => f.id === fieldId);
    if (!field) return;
    const opts = [...(field.options || [])];
    const n = opts.length + 1;
    opts.push({ label: `Option ${n}`, value: `opt${n}` });
    updateField(fieldId, { options: opts });
  }

  function updateOption(fieldId, optIdx, key, val) {
    const field = getLiveFields().find((f) => f.id === fieldId);
    if (!field) return;
    const opts = (field.options || []).map((o, i) => i === optIdx ? { ...o, [key]: val } : o);
    updateField(fieldId, { options: opts });
  }

  function removeOption(fieldId, optIdx) {
    const field = getLiveFields().find((f) => f.id === fieldId);
    if (!field) return;
    updateField(fieldId, { options: (field.options || []).filter((_, i) => i !== optIdx) });
  }

  // ── Submissions state ──────────────────────────────────────
  const formId = data.formId;
  const builtInUrl = `${API_BASE}/submit/${formId}?customer_id=${JSON.parse(localStorage.getItem('customer')).customer_id}`;
  const [copied, setCopied]           = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [subCount, setSubCount]       = useState(null);
  const [subLoading, setSubLoading]   = useState(false);
  const [showSubs, setShowSubs]       = useState(false);

  async function fetchSubmissions() {
    setSubLoading(true);
    try {
      const { data: json } = await api.get(`/forms/${formId}/submissions`);
      if (json.success) {
        setSubmissions(json.data);
        setSubCount(json.total);
      }
    } catch { /* ignore */ } finally {
      setSubLoading(false);
    }
  }

  async function clearSubmissions() {
    await api.delete(`/forms/${formId}/submissions`);
    setSubmissions([]);
    setSubCount(0);
  }

  useEffect(() => {
    api.get(`/forms/${formId}/submissions`)
      .then(({ data: j }) => { if (j.success) setSubCount(j.total); })
      .catch(() => {});
  }, [formId]);

  function copyUrl() {
    navigator.clipboard.writeText(builtInUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const Section = ({ title }) => (
    <p className="text-xs font-semibold uppercase tracking-wider mb-1.5 mt-3 first:mt-0"
       style={{ color: 'var(--t-muted)' }}>{title}</p>
  );

  return (
    <div className="p-2 space-y-2 text-xs">
      <p className="panel-label">Form Builder</p>

      {/* ── Form Title ───────────────────────────────── */}
      <Section title="Form" />
      <div>
        <label className="block mb-0.5" style={{ color: 'var(--t-muted)' }}>Form Title (optional)</label>
        <input
          type="text"
          value={data.formTitle || ''}
          onChange={(e) => update({ formTitle: e.target.value })}
          className="input-field"
          placeholder="e.g. Contact Us"
        />
      </div>

      {/* ── Submission endpoint ───────────────────────── */}
      <Section title="Submission" />

      {/* Built-in endpoint */}
      <div className="rounded-lg p-2.5 space-y-1.5" style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)' }}>
        <div className="flex items-center justify-between">
          <p className="font-semibold" style={{ color: '#a5b4fc' }}>✅ Built-in Endpoint</p>
          <span className="px-1.5 py-0.5 rounded text-xs font-bold" style={{ background: '#10b981', color: '#fff', fontSize: 9 }}>READY</span>
        </div>
        <p style={{ color: 'var(--t-muted)', fontSize: 10, lineHeight: 1.4 }}>
          Submissions go to your local backend and are stored automatically. Copy the URL below and paste it into Action-XHR.
        </p>
        <div className="flex gap-1 items-center">
          <code className="flex-1 rounded px-2 py-1 text-xs truncate font-mono"
                style={{ background: 'rgba(0,0,0,0.3)', color: '#e2e8f0', fontSize: 10 }}>
            {builtInUrl}
          </code>
          <button
            onClick={copyUrl}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold flex-shrink-0 transition-colors"
            style={{ background: copied ? '#10b981' : PRIMARY_BG, color: '#fff' }}
          >
            {copied ? <><Check size={11} /> Copied!</> : <><Copy size={11} /> Copy</>}
          </button>
        </div>
        <button
          onClick={() => update({ actionXhrUrl: builtInUrl })}
          className="w-full py-1 rounded text-xs font-semibold transition-opacity hover:opacity-80"
          style={{ background: 'rgba(99,102,241,0.3)', color: 'black' }}
        >
          Use this endpoint ↑
        </button>
      </div>

      {/* Custom endpoint */}
      <div>
        <label className="block mb-0.5" style={{ color: 'var(--t-muted)' }}>Action-XHR URL <span style={{ opacity: 0.6 }}>(CORS endpoint)</span></label>
        <input
          type="url" value={data.actionXhrUrl || ''}
          onChange={(e) => update({ actionXhrUrl: e.target.value })}
          className="input-field font-mono"
          placeholder="https://api.example.com/submit"
        />
      </div>
      <div>
        <label className="block mb-0.5" style={{ color: 'var(--t-muted)' }}>Fallback Action URL</label>
        <input
          type="url" value={data.actionUrl || ''}
          onChange={(e) => update({ actionUrl: e.target.value })}
          className="input-field font-mono"
          placeholder="https://example.com/form"
        />
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <label className="block mb-0.5" style={{ color: 'var(--t-muted)' }}>Success Message</label>
          <input
            type="text" value={data.successMessage || ''}
            onChange={(e) => update({ successMessage: e.target.value })}
            className="input-field"
            placeholder="Thank you!"
          />
        </div>
        <div>
          <label className="block mb-0.5" style={{ color: 'var(--t-muted)' }}>Error Message</label>
          <input
            type="text" value={data.errorMessage || ''}
            onChange={(e) => update({ errorMessage: e.target.value })}
            className="input-field"
            placeholder="Something went wrong."
          />
        </div>
      </div>

      {/* ── Submissions Viewer ─────────────────────────── */}
      <Section title={`Submissions${subCount !== null ? ` (${subCount})` : ''}`} />
      <button
        onClick={() => { setShowSubs(!showSubs); if (!showSubs) fetchSubmissions(); }}
        className="w-full flex items-center justify-between px-2 py-1.5 rounded transition-colors"
        style={{ background: 'var(--t-panel)', border: '1px solid var(--t-border)', color: 'var(--t-text2)' }}
      >
        <span className="flex items-center gap-1.5">
          <Inbox size={13} />
          {showSubs ? 'Hide submissions' : `View submissions${subCount ? ` (${subCount})` : ''}`}
        </span>
        <div className="flex items-center gap-1">
          {showSubs && (
            <button onClick={(e) => { e.stopPropagation(); fetchSubmissions(); }}
                    className="p-0.5 hover:text-indigo-400" title="Refresh"
                    style={{ color: 'var(--t-muted)' }}>
              <RefreshCw size={11} className={subLoading ? 'animate-spin' : ''} />
            </button>
          )}
        </div>
      </button>

      {showSubs && (
        <div className="space-y-1.5">
          {subLoading && <p className="text-center py-2" style={{ color: 'var(--t-muted)', fontSize: 11 }}>Loading…</p>}
          {!subLoading && submissions.length === 0 && (
            <p className="text-center py-3" style={{ color: 'var(--t-muted)', fontSize: 11 }}>
              No submissions yet.<br />
              <span style={{ opacity: 0.6 }}>Use the built-in endpoint above.</span>
            </p>
          )}
          {submissions.map((sub) => (
            <div key={sub.id} className="rounded p-2 space-y-1 text-xs" style={{ background: 'var(--t-panel)', border: '1px solid var(--t-border)' }}>
              <div className="flex items-center justify-between">
                <span style={{ color: 'var(--t-muted)', fontSize: 10 }}>
                  {new Date(sub.submittedAt).toLocaleString()}
                </span>
              </div>
              {Object.entries(sub.data).map(([key, val]) => (
                <div key={key} className="flex gap-1.5">
                  <span className="font-mono font-semibold flex-shrink-0" style={{ color: '#a5b4fc', minWidth: 60 }}>{key}</span>
                  <span className="truncate" style={{ color: 'var(--t-text2)' }}>{String(val)}</span>
                </div>
              ))}
            </div>
          ))}
          {submissions.length > 0 && (
            <button
              onClick={clearSubmissions}
              className="w-full py-1 rounded text-xs hover:opacity-80"
              style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' }}
            >
              Clear all submissions
            </button>
          )}
        </div>
      )}

      {/* ── Style ─────────────────────────────────────── */}
      <Section title="Style" />

      {[
        { key: 'backgroundColor', label: 'Background' },
        { key: 'textColor',       label: 'Text Color' },
        { key: 'accentColor',     label: 'Accent Color' },
        { key: 'buttonColor',     label: 'Button Color' },
      ].map(({ key, label }) => (
        <div key={key}>
          <label className="block mb-0.5" style={{ color: 'var(--t-muted)' }}>{label}</label>
          <div className="flex gap-1 items-center">
            <div
              className="w-7 h-7 rounded cursor-pointer flex-shrink-0"
              style={{ background: sanitizeHex(style[key]), border: '1px solid var(--t-border2)' }}
              onClick={() => setColorPickerFor(colorPickerFor === key ? null : key)}
            />
            <input
              type="text"
              value={style[key] || ''}
              onChange={(e) => update({ style: { ...style, [key]: e.target.value } })}
              className="input-field flex-1 font-mono"
              placeholder="#000000"
            />
          </div>
          {colorPickerFor === key && (
            <div className="mt-2">
              <HexColorPicker
                color={sanitizeHex(style[key])}
                onChange={(c) => update({ style: { ...style, [key]: c } })}
                style={{ width: '100%', height: 120 }}
              />
            </div>
          )}
        </div>
      ))}

      <div>
        <label className="flex items-center justify-between mb-0.5">
          <span style={{ color: 'var(--t-muted)' }}>Background Opacity</span>
          <span style={{ color: 'var(--t-text2)' }}>{style.backgroundOpacity ?? 90}%</span>
        </label>
        <input
          type="range" min={0} max={100} value={style.backgroundOpacity ?? 90}
          onChange={(e) => update({ style: { ...style, backgroundOpacity: Number(e.target.value) } })}
          className="w-full"
        />
      </div>

      <div>
        <label className="flex items-center justify-between mb-0.5">
          <span style={{ color: 'var(--t-muted)' }}>Corner Radius</span>
          <span style={{ color: 'var(--t-text2)' }}>{style.borderRadius}px</span>
        </label>
        <input
          type="range" min={0} max={40} value={style.borderRadius}
          onChange={(e) => update({ style: { ...style, borderRadius: Number(e.target.value) } })}
          className="w-full"
        />
      </div>

      {/* ── Fields ────────────────────────────────────── */}
      <Section title={`Fields (${fields.length})`} />

      {fields.length === 0 && (
        <p className="text-center py-3" style={{ color: 'var(--t-muted)' }}>
          No fields yet — add one below
        </p>
      )}

      <div className="space-y-1">
        {fields.map((field, idx) => {
          const isOpen = expandedField === field.id;
          const color = FIELD_TYPE_COLORS[field.type] || '#6b7280';
          return (
            <div key={field.id} className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--t-border)', background: 'var(--t-panel)' }}>
              {/* Field header row */}
              <div
                className="flex items-center gap-1.5 px-2 py-1.5 cursor-pointer select-none"
                onClick={() => setExpandedField(isOpen ? null : field.id)}
              >
                <GripVertical size={12} style={{ color: 'var(--t-muted)', flexShrink: 0 }} />
                <span className="rounded px-1.5 py-0.5 font-bold uppercase" style={{ background: color + '22', color, fontSize: 9, letterSpacing: 0.5 }}>
                  {FIELD_TYPE_LABELS[field.type]}
                </span>
                <span className="flex-1 truncate" style={{ color: 'var(--t-text2)' }}>{field.label || '(no label)'}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); moveField(field.id, -1); }}
                  disabled={idx === 0}
                  className="p-0.5 disabled:opacity-30 hover:text-white"
                  style={{ color: 'var(--t-muted)' }}
                  title="Move up"
                >
                  <ChevronUp size={12} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); moveField(field.id, 1); }}
                  disabled={idx === fields.length - 1}
                  className="p-0.5 disabled:opacity-30 hover:text-white"
                  style={{ color: 'var(--t-muted)' }}
                  title="Move down"
                >
                  <ChevronDown size={12} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); removeField(field.id); }}
                  className="p-0.5 hover:text-red-400"
                  style={{ color: 'var(--t-muted)' }}
                  title="Delete field"
                >
                  <Trash2 size={11} />
                </button>
              </div>

              {/* Expanded field editor */}
              {isOpen && (
                <div className="px-2 pb-2 pt-0 space-y-1.5 border-t" style={{ borderColor: 'var(--t-border)' }}>
                  {/* Label */}
                  <div>
                    <label className="block mb-0.5" style={{ color: 'var(--t-muted)' }}>Label</label>
                    <input
                      type="text" value={field.label || ''}
                      onChange={(e) => updateField(field.id, { label: e.target.value })}
                      className="input-field"
                      placeholder="Field label"
                    />
                  </div>

                  {/* Field name (id attribute) */}
                  <div>
                    <label className="block mb-0.5" style={{ color: 'var(--t-muted)' }}>Field Name <span style={{ opacity: 0.5 }}>(HTML name attr)</span></label>
                    <input
                      type="text" value={field.name || ''}
                      onChange={(e) => updateField(field.id, { name: e.target.value })}
                      className="input-field font-mono"
                      placeholder="field_name"
                    />
                  </div>

                  {/* Placeholder (not for submit, checkbox, radio, hidden) */}
                  {!['submit', 'checkbox', 'radio', 'hidden'].includes(field.type) && (
                    <div>
                      <label className="block mb-0.5" style={{ color: 'var(--t-muted)' }}>Placeholder</label>
                      <input
                        type="text" value={field.placeholder || ''}
                        onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                        className="input-field"
                        placeholder="Placeholder text"
                      />
                    </div>
                  )}

                  {/* Default value for hidden */}
                  {field.type === 'hidden' && (
                    <div>
                      <label className="block mb-0.5" style={{ color: 'var(--t-muted)' }}>Value <span style={{ opacity: 0.5 }}>(hidden)</span></label>
                      <input
                        type="text" value={field.defaultValue || ''}
                        onChange={(e) => updateField(field.id, { defaultValue: e.target.value })}
                        className="input-field font-mono"
                        placeholder="hidden value"
                      />
                    </div>
                  )}

                  {/* Required toggle */}
                  {!['submit', 'hidden'].includes(field.type) && (
                    <label className="flex items-center gap-2 cursor-pointer py-0.5">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(field.id, { required: e.target.checked })}
                      />
                      <span style={{ color: 'var(--t-text2)' }}>Required field</span>
                    </label>
                  )}

                  {/* Options editor for select / radio */}
                  {(field.type === 'select' || field.type === 'radio') && (
                    <div>
                      <label className="block mb-1" style={{ color: 'var(--t-muted)' }}>Options</label>
                      <div className="space-y-1 mb-1">
                        {(field.options || []).map((opt, oi) => (
                          <div key={oi} className="flex gap-1 items-center">
                            <input
                              type="text" value={opt.label}
                              onChange={(e) => updateOption(field.id, oi, 'label', e.target.value)}
                              className="input-field"
                              style={{ flex: '1 1 0', minWidth: 0, width: 0 }}
                              placeholder="Label"
                            />
                            <input
                              type="text" value={opt.value}
                              onChange={(e) => updateOption(field.id, oi, 'value', e.target.value)}
                              className="input-field font-mono"
                              style={{ width: '4.5rem', flexShrink: 0 }}
                              placeholder="value"
                            />
                            <button
                              onClick={() => removeOption(field.id, oi)}
                              className="hover:text-red-400 flex-shrink-0"
                              style={{ color: 'var(--t-muted)' }}
                            >
                              <Trash2 size={11} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => addOption(field.id)}
                        className="flex items-center gap-1 px-2 py-1 rounded w-full justify-center hover:opacity-80 transition-opacity"
                        style={{ background: 'var(--t-panel2)', color: 'var(--t-muted)', border: '1px dashed var(--t-border)' }}
                      >
                        <Plus size={10} /> Add Option
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Add Field Buttons ────────────────────────── */}
      <div>
        <p className="mb-1.5" style={{ color: 'var(--t-muted)' }}>Add Field</p>
        <div className="flex flex-wrap gap-1">
          {FIELD_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => addField(t)}
              className="flex items-center gap-1 px-2 py-1 rounded transition-all hover:scale-105"
              style={{
                background: FIELD_TYPE_COLORS[t] + '18',
                color: FIELD_TYPE_COLORS[t],
                border: `1px solid ${FIELD_TYPE_COLORS[t]}40`,
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              <Plus size={9} />
              {FIELD_TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Ensure we always pass a valid 6-digit hex to HexColorPicker (handles legacy rgba values)
function sanitizeHex(color) {
  if (!color) return '#0f0f1e';
  if (color.startsWith('#')) return color.slice(0, 7);
  const m = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!m) return '#0f0f1e';
  return '#' + [m[1], m[2], m[3]].map((n) => parseInt(n).toString(16).padStart(2, '0')).join('');
}
